import React, { useEffect } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { CollapsibleSection } from 'cpool-components/general/CollapsibleSection';
import { APPLICANT_ATTRIBUTION_BOTH, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { APPLICANT_ATTRIBUTION_BOTH_TYPE } from 'cpool-constants';
import { Baubeschreibung } from 'cpool-schemas';
import type { ParseKeys } from 'i18next';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ItemDeleteButton from '../General/ItemDeleteButton.tsx';
import {
    type BuildingDescriptionStepId,
    DEFAULT_BUILDING_DESCRIPTION_STEP_ID,
} from '~/features/leadDetails/components/Overview/CustomerData/RealEstates/BuildingDescription/types';
import { syncBuildingDescriptionFromRealEstate } from '~/features/leadDetails/components/Overview/CustomerData/RealEstates/buildingDescriptionTransfer';
import { useRealEstateSelfOccupation } from '~/features/leadDetails/components/Overview/CustomerData/RealEstates/hooks/useRealEstateSelfOccupation';
import { RealEstateDetails } from '~/features/leadDetails/components/Overview/CustomerData/RealEstates/RealEstateDetails';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema.ts';
import { canExecuteForAttribution } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    buildingDescriptionStep?: BuildingDescriptionStepId;
    currentApplicantAttribution: APPLICANT_ATTRIBUTION_BOTH_TYPE;
    hasSecondApplicant: boolean;
    removeRealEstate: (index: number, applicantAttribution: APPLICANT_ATTRIBUTION_BOTH_TYPE) => void;
    realEstateCount: number;
    realEstateIndex: number;
    translationPath: ParseKeys<'de'>;
    onApplicantAttributionChange: (index: number, value: APPLICANT_ATTRIBUTION_BOTH_TYPE) => void;
    onBuildingDescriptionRemove?: () => void;
    onBuildingDescriptionStepChange?: (step: BuildingDescriptionStepId) => void;
};

const RealEstate: React.FC<Props> = ({
    buildingDescriptionStep,
    currentApplicantAttribution,
    hasSecondApplicant,
    onApplicantAttributionChange,
    onBuildingDescriptionRemove,
    onBuildingDescriptionStepChange,
    realEstateCount,
    realEstateIndex,
    removeRealEstate,
    translationPath,
}) => {
    const { t } = useTranslation();
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { control, getValues, setValue } = useFormContext<SchemaRealEstatesForm>();
    const realEstateName = `immobilien.${realEstateIndex}` as const;
    const buildingDescriptionName = `${realEstateName}.baubeschreibung` as const;
    const { field: buildingDescriptionField } = useController({
        control,
        name: buildingDescriptionName,
    });
    const [estimatedValue, rentedLivingSpace, livingSpace] = useWatch({
        control,
        name: [`${realEstateName}.wert`, `${realEstateName}.vermieteteWohnflaeche`, `${realEstateName}.wohnflaeche`],
    });
    const buildingAddress = useWatch({
        control,
        name: `${buildingDescriptionName}.anschriftUndObjektart.anschrift`,
    });
    const { changeApplicantAttribution, onUsageTypeChange, usageType } = useRealEstateSelfOccupation({
        currentApplicantAttribution,
        onApplicantAttributionChange,
        realEstateIndex,
    });

    const isChangeDisabled = !canExecuteForAttribution({
        attribution: currentApplicantAttribution,
        commandName: LOAN_REQUEST_COMMANDS.changeRealEstate,
        permissions,
    });
    const isBuildingDescriptionChangeDisabled = !canExecuteForAttribution({
        attribution: currentApplicantAttribution,
        commandName: LOAN_REQUEST_COMMANDS.changeRealEstateBuildingDescription,
        permissions,
    });
    const isApplicantAttributionChangeDisabled =
        isChangeDisabled ||
        !canExecuteForAttribution({
            attribution: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2,
            commandName: LOAN_REQUEST_COMMANDS.changeRealEstate,
            permissions,
        });
    const hasBuildingDescription = buildingDescriptionField.value != null;
    const street = [buildingAddress?.strasse, buildingAddress?.hausnummer].filter(Boolean).join(' ');
    const cityLine = [buildingAddress?.plz, buildingAddress?.ort].filter(Boolean).join(' ');
    const formattedBuildingAddress = [street, cityLine].filter(Boolean).join(', ');
    const headerTitle = formattedBuildingAddress
        ? t('REAL_ESTATE.LABELS.HEADER_WITH_ADDRESS', {
              address: formattedBuildingAddress,
              count: realEstateCount,
          })
        : t(translationPath, { count: realEstateCount });

    useEffect(() => {
        const realEstate = getValues(realEstateName);
        const { baubeschreibung } = realEstate;
        if (baubeschreibung == null) return;

        const syncedBuildingDescription = syncBuildingDescriptionFromRealEstate({ baubeschreibung, realEstate });
        const currentAddressAndType = baubeschreibung.anschriftUndObjektart;
        const syncedAddressAndType = syncedBuildingDescription.anschriftUndObjektart;
        if (
            currentAddressAndType.geschaetzterWert === syncedAddressAndType.geschaetzterWert &&
            currentAddressAndType.fremdgenutzteWohnflaeche === syncedAddressAndType.fremdgenutzteWohnflaeche &&
            currentAddressAndType.wohnflaeche === syncedAddressAndType.wohnflaeche
        ) {
            return;
        }

        setValue(buildingDescriptionName, syncedBuildingDescription, {
            shouldDirty: false,
            shouldValidate: true,
        });
    }, [
        buildingDescriptionName,
        estimatedValue,
        getValues,
        hasBuildingDescription,
        livingSpace,
        realEstateName,
        rentedLivingSpace,
        setValue,
        usageType,
    ]);

    const addBuildingDescription = () => {
        const realEstate = getValues(realEstateName);
        buildingDescriptionField.onChange(
            syncBuildingDescriptionFromRealEstate({
                baubeschreibung: Baubeschreibung.parse({}),
                realEstate,
            }),
        );
        onBuildingDescriptionStepChange?.(DEFAULT_BUILDING_DESCRIPTION_STEP_ID);
    };

    const removeBuildingDescription = () => {
        if (onBuildingDescriptionRemove) onBuildingDescriptionRemove();
        else onBuildingDescriptionStepChange?.(DEFAULT_BUILDING_DESCRIPTION_STEP_ID);
        buildingDescriptionField.onChange(null);
    };

    return (
        <CollapsibleSection
            bgClassNames="rounded bg-white border-l-2 border-blue p-0"
            defaultOpen
            headerChildren={
                <ItemDeleteButton
                    onClick={() => removeRealEstate(realEstateIndex, currentApplicantAttribution)}
                    translationPath="REAL_ESTATE.BUTTONS.DELETE_REAL_ESTATE"
                    disabled={
                        !canExecuteForAttribution({
                            attribution: currentApplicantAttribution,
                            commandName: LOAN_REQUEST_COMMANDS.removeRealEstate,
                            permissions,
                        })
                    }
                />
            }
            headerClassNames="h-[45px] px-5"
            keepMounted
            testId={`immobilien.${realEstateIndex}`}
            title={headerTitle}
            titleClassNames="text-sm font-bold text-midnight"
        >
            <RealEstateDetails
                buildingDescriptionStep={buildingDescriptionStep}
                currentApplicantAttribution={currentApplicantAttribution}
                hasBuildingDescription={hasBuildingDescription}
                hasSecondApplicant={hasSecondApplicant}
                isApplicantAttributionChangeDisabled={isApplicantAttributionChangeDisabled}
                isBuildingDescriptionChangeDisabled={isBuildingDescriptionChangeDisabled}
                isChangeDisabled={isChangeDisabled}
                onAddBuildingDescription={addBuildingDescription}
                onApplicantAttributionChange={changeApplicantAttribution}
                onBuildingDescriptionStepChange={onBuildingDescriptionStepChange}
                onRemoveBuildingDescription={removeBuildingDescription}
                onUsageTypeChange={onUsageTypeChange}
                realEstateIndex={realEstateIndex}
                usageType={usageType}
            />
        </CollapsibleSection>
    );
};

export default RealEstate;
